import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Reveal from "../components/Reveal";
import { CONTACT_INFO, LEGAL } from "../mock";

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-serif-brand text-2xl md:text-3xl text-purple mb-3">{title}</h2>
      <div className="space-y-3 text-neutral-600 leading-relaxed text-[0.98rem]">{children}</div>
    </div>
  );
}

export default function Privacy() {
  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-[#effaf9] to-white pt-16 pb-10 md:pt-20 md:pb-14">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Reveal>
            <ShieldCheck className="mx-auto text-[var(--teal-dark)] mb-4" size={32} />
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">
              {CONTACT_INFO.brand}
            </p>
            <h1 className="font-serif-brand text-5xl md:text-6xl text-purple mt-3">
              Privacy Policy & Disclaimer
            </h1>
            <div className="brand-rule mx-auto mt-5" />
            <p className="text-neutral-500 mt-5 text-sm">Last updated: {CONTACT_INFO.year}</p>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <Reveal>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[var(--teal-dark)] font-semibold tracking-wide-sm uppercase text-xs mb-10 hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>

          <p className="text-neutral-600 leading-relaxed mb-10 italic">
            Your privacy matters to us. This page explains how {CONTACT_INFO.brand} collects, uses,
            and protects your information, and outlines important disclaimers about the content on
            this website.
          </p>

          <Section title="Copyright & Use of Content">
            <p>{LEGAL.privacyText}</p>
          </Section>

          <Section title="Information We Collect">
            <p>
              We only collect the information you choose to share with us, for example, when you
              book a consultation, request your free astrology chart, or contact us by email or
              phone. This may include your name, email address, phone number, and any details you
              provide about your wellness goals.
            </p>
          </Section>

          <Section title="How We Use Your Information">
            <p>
              We use your information solely to respond to your inquiries, schedule and provide our
              services, and, with your permission, to occasionally share meaningful, relevant
              information. By interacting with this site you authorize Kamla Das of{" "}
              {CONTACT_INFO.brand} to contact you from time to time with meaningful information. You
              may discontinue this at any time by emailing us at{" "}
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="text-[var(--teal-dark)] font-semibold hover:underline"
              >
                {CONTACT_INFO.email}
              </a>
              .
            </p>
          </Section>

          <Section title="Third Party Services">
            <p>
              To serve you better, we use trusted third party tools such as zcal (for scheduling
              consultations) and Google Forms (for the free astrology chart request). When you use
              these features, your information is handled according to those providers' own privacy
              policies. We never sell or rent your personal information to anyone.
            </p>
          </Section>

          <Section title="Health & Wellness Disclaimer">
            <p>
              The content on this website, including articles, guided meditations, and wellness
              information, is provided for educational and informational purposes only. It is not
              intended as medical advice, diagnosis, or treatment, and it does not replace guidance
              from your physician or a qualified health professional. Always consult a licensed
              professional regarding any medical condition or before beginning any new wellness
              practice.
            </p>
          </Section>

          <Section title="Your Choices">
            <p>
              You may request to review, update, or delete the personal information we hold about
              you, and you may opt out of future communications at any time. Simply reach out and we
              will gladly assist.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              If you have any questions about this Privacy Policy, please contact:
            </p>
            <p className="text-neutral-700">
              {CONTACT_INFO.brand}
              <br />
              {CONTACT_INFO.location}
              <br />
              Email:{" "}
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="text-[var(--teal-dark)] font-semibold hover:underline"
              >
                {CONTACT_INFO.email}
              </a>
              <br />
              Cell:{" "}
              <a
                href={`tel:+1${CONTACT_INFO.phone.replace(/\s/g, "")}`}
                className="text-[var(--teal-dark)] font-semibold hover:underline"
              >
                {CONTACT_INFO.phone}
              </a>
            </p>
          </Section>
        </Reveal>
      </section>
    </div>
  );
}
